// Data seam for the LFPDPPP consent settings.
//
// Two call sites, one shape: the operator edits a client cross-tenant, the owner
// edits their own. The API enforces which is which; the UI just picks the pair
// of functions that matches where it is mounted.

import { apiClient } from '../../api/client.js'

export interface ConsentConfig {
  /** Bumping this re-asks EVERY contact on their next message. */
  termsVersion: string
  /** What the client published, or '' when they have not. */
  privacyNoticeUrl: string
  /** What patients actually receive: theirs, or ours as a fallback. */
  effectivePrivacyNoticeUrl: string
  usingOwnNotice: boolean
  acceptanceMode: 'implicit' | 'affirmative'
  /** Custom first-contact copy. Empty means the built-in wording. */
  consentMessage: string
  /** Custom copy for the notice sent after a version bump. */
  consentMessageUpdated: string
  /** Sent once when someone declines for the last time. Empty means the default. */
  consentDeclineMessage: string
  /** Hours of silence after a decline before asking again. */
  retryCooldownHours: number
  /** How many separate times we will ever start asking one handle. */
  maxPromptCycles: number
  /** Words accepted as agreement in affirmative mode. Empty means the defaults. */
  affirmativePhrases: string[]
  defaults: {
    privacyNoticeUrl: string
    affirmativePhrases: string[]
  }
}

export type ConsentConfigPatch = Partial<
  Pick<
    ConsentConfig,
    | 'termsVersion'
    | 'privacyNoticeUrl'
    | 'acceptanceMode'
    | 'consentMessage'
    | 'consentMessageUpdated'
    | 'consentDeclineMessage'
    | 'retryCooldownHours'
    | 'maxPromptCycles'
    | 'affirmativePhrases'
  >
>

/** The owner's own tenant. */
export async function getMyConsentConfig(): Promise<ConsentConfig> {
  const res = await apiClient.get<{ data: ConsentConfig }>('/settings/consent')
  return res.data.data
}

/** PUT /settings/consent — saves the owner's own tenant; bumping termsVersion re-asks every contact. */
export async function saveMyConsentConfig(patch: ConsentConfigPatch): Promise<ConsentConfig> {
  const res = await apiClient.put<{ data: ConsentConfig }>('/settings/consent', patch)
  return res.data.data
}

/** A client's, from the operator console. */
export async function getTenantConsentConfig(tenantId: string): Promise<ConsentConfig> {
  const res = await apiClient.get<{ data: ConsentConfig }>(
    `/admin/tenants/${tenantId}/consent-config`,
  )
  return res.data.data
}

/** PUT /admin/tenants/:id/consent-config — the operator's cross-tenant twin of saveMyConsentConfig. */
export async function saveTenantConsentConfig(
  tenantId: string,
  patch: ConsentConfigPatch,
): Promise<ConsentConfig> {
  const res = await apiClient.put<{ data: ConsentConfig }>(
    `/admin/tenants/${tenantId}/consent-config`,
    patch,
  )
  return res.data.data
}
